import React, { useEffect, useRef, useState } from 'react';

import AppendixIndexBlock from './blocks/AppendixIndexBlock';
import BasisBlock from './blocks/BasisBlock';
import ChartBlock from './blocks/ChartBlock';
import CommentaryBlock from './blocks/CommentaryBlock';
import ContentsBlock from './blocks/ContentsBlock';
import EmptyStateBlock from './blocks/EmptyStateBlock';
import MatrixBlock from './blocks/MatrixBlock';
import NoteBlock from './blocks/NoteBlock';
import OrgChartBlock from './blocks/OrgChartBlock';
import StatusRulesBlock from './blocks/StatusRulesBlock';
import TableBlock from './blocks/TableBlock';
import TilesBlock from './blocks/TilesBlock';
import CoverSlide from './CoverSlide';
import {
    box,
    CONTENT_WIDTH,
    FOOTER_TOP,
    HEADER_LINE_HEIGHT,
    HEADER_TAG_WIDTH,
    HEADER_TOP,
    MARGIN_X,
    pt,
    SLIDE_HEIGHT,
    SLIDE_WIDTH,
    TITLE_TOP,
} from './slideGeometry';

const BLOCKS = {
    tiles: TilesBlock,
    table: TableBlock,
    chart: ChartBlock,
    basis: BasisBlock,
    emptyState: EmptyStateBlock,
    contents: ContentsBlock,
    note: NoteBlock,
    statusRules: StatusRulesBlock,
    matrix: MatrixBlock,
    appendixIndex: AppendixIndexBlock,
    orgChart: OrgChartBlock,
};

/**
 * The tag, as-at date and note, title and subtitle, at the sizes and in the boxes the Paginator fitted
 * them to (`page.header`). Text wraps rather than being cut.
 */
const SlideHeader = ({ page }) => {
    const { header } = page;
    const asAtWidth = CONTENT_WIDTH - HEADER_TAG_WIDTH;

    return (
        <>
            {page.sectionTag && (
                <div
                    className="tw-text-blue tw-font-bold tw-uppercase"
                    style={{ ...box(MARGIN_X, HEADER_TOP, HEADER_TAG_WIDTH, HEADER_LINE_HEIGHT), fontSize: pt(header.tagSize) }}
                >
                    {page.sectionTag}
                </div>
            )}
            {page.asAt && (
                <div
                    className="tw-text-charcoal-60 tw-text-end"
                    style={{ ...box(MARGIN_X + HEADER_TAG_WIDTH, HEADER_TOP, asAtWidth, HEADER_LINE_HEIGHT), fontSize: pt(header.asAtSize) }}
                >
                    {$t('As at {{date}}', { date: page.asAtLabel || page.asAt })}
                </div>
            )}
            {page.asAtNote && (
                <div
                    className="tw-text-charcoal-60 tw-text-end"
                    style={{ ...box(MARGIN_X + HEADER_TAG_WIDTH, TITLE_TOP, asAtWidth, HEADER_LINE_HEIGHT), fontSize: pt(header.asAtNoteSize) }}
                >
                    {page.asAtNote}
                </div>
            )}
            <div
                className="tw-text-charcoal tw-font-bold"
                style={{ ...box(MARGIN_X, TITLE_TOP, header.titleWidth, header.titleHeight), fontSize: pt(header.titleSize) }}
            >
                {page.title}
            </div>
            {page.subtitle && (
                <div
                    className="tw-text-charcoal-60"
                    style={{ ...box(MARGIN_X, header.subtitleTop, CONTENT_WIDTH, header.subtitleHeight), fontSize: pt(header.subtitleSize) }}
                >
                    {page.subtitle}
                </div>
            )}
        </>
    );
};

/**
 * The footer: the confidentiality label and event and period where and at the size the Paginator fitted
 * them to (`footer.fit`), then the page number.
 */
const SlideFooter = ({ footer = {} }) => {
    const third = Math.floor(CONTENT_WIDTH / 3);
    const style = (x, width, fit = { size: 8, top: FOOTER_TOP + 10, height: 14 }) => ({ ...box(x, fit.top, width, fit.height), fontSize: pt(fit.size) });

    return (
        <>
            <div className="tw-bg-charcoal-15" style={box(MARGIN_X, FOOTER_TOP, CONTENT_WIDTH, 1)} />
            <div className="tw-text-charcoal-60" style={style(MARGIN_X, third, footer.fit?.confidentiality)}>
                {footer.confidentiality}
            </div>
            <div className="tw-text-charcoal-60 tw-text-center" style={style(MARGIN_X + third, third, footer.fit?.eventPeriod)}>
                {footer.eventPeriod}
            </div>
            {footer.pageNumber !== undefined && (
                <div className="tw-text-charcoal-60 tw-text-end" style={style(MARGIN_X + 2 * third, CONTENT_WIDTH - 2 * third)}>
                    {footer.pageNumber} / {footer.pageCount}
                </div>
            )}
        </>
    );
};

const Watermark = () => (
    <div
        aria-hidden="true"
        className="tw-flex tw-items-center tw-justify-center tw-font-bold tw-text-charcoal-15 tw-pointer-events-none tw-select-none"
        style={{ ...box((SLIDE_WIDTH - 720) / 2, (SLIDE_HEIGHT - 200) / 2, 720, 200), fontSize: pt(120), transform: 'rotate(-30deg)' }}
    >
        DRAFT
    </div>
);

/**
 * Tracks an element's width, for scaling the slide to its container.
 */
const useWidth = () => {
    const ref = useRef(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        if (!ref.current) {
            return undefined;
        }

        const element = ref.current;
        setWidth(element.clientWidth);

        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const observer = new ResizeObserver(() => setWidth(element.clientWidth));
        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    return [ref, width];
};

/**
 * One physical page on a 960 x 540 canvas, scaled to the container width with a
 * CSS transform so every block sits exactly where the .pptx puts it. The slide
 * is always left to right, like the file it previews.
 */
const SlideCanvas = ({ page, watermark = false, thumbnail = false, commentaryEditable = false, onSaveCommentary, label }) => {
    const [ref, width] = useWidth();
    const scale = width ? width / SLIDE_WIDTH : 0;

    return (
        <div ref={ref} className="tw-w-full tw-relative tw-overflow-hidden" style={{ height: SLIDE_HEIGHT * scale }} role="group" aria-label={label}>
            {scale > 0 && (
                <div
                    dir="ltr"
                    className="tw-absolute tw-top-0 tw-bg-white tw-text-start tw-font-sans"
                    style={{ left: 0, width: SLIDE_WIDTH, height: SLIDE_HEIGHT, lineHeight: 1.2, transform: `scale(${scale})`, transformOrigin: 'top left' }}
                >
                    {watermark && <Watermark />}
                    {page.kind === 'cover' ? (
                        <CoverSlide page={page} />
                    ) : (
                        <>
                            <SlideHeader page={page} />
                            {(page.blocks || []).map((block, index) => {
                                const Block = BLOCKS[block.type];

                                return Block ? <Block key={`${block.type}-${index}`} block={block} thumbnail={thumbnail} /> : null;
                            })}
                            {page.commentary && (
                                <CommentaryBlock
                                    commentary={page.commentary}
                                    frame={(page.blocks || []).find((block) => block.type === 'commentary')}
                                    editable={!thumbnail && commentaryEditable}
                                    onSave={onSaveCommentary}
                                />
                            )}
                        </>
                    )}
                    <SlideFooter footer={page.footer} />
                </div>
            )}
        </div>
    );
};

export default SlideCanvas;
